import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { post, TENANT_STORAGE_KEY, tenantStore } from "./api";
import { useAuth } from "./auth";
import type { Tenant } from "./types";

interface TenantContextValue {
  current: Tenant | null;
  available: Tenant[];
  switchTo: (id: number) => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const available = user?.tenants ?? [];
  // Prefer the membership-list entry for the active tenant: it carries the
  // `pivot.role` (the tenant_user role) that nav/route gating depends on.
  // `current_tenant` is a belongsTo with no pivot, so using it first left
  // role-gated items (e.g. Admin panel) hidden even for owners.
  //
  // The tenant shown must be the one every request sends (X-Tenant-Id from
  // tenantStore, audit D3) — users.current_tenant_id is shared by every tab
  // and device, so it's only a fallback (audit FE-14/FE-15).
  const headerTenant = tenantStore.get();
  const current =
    available.find((t) => t.id === headerTenant) ??
    available.find((t) => t.id === user?.current_tenant_id) ??
    user?.current_tenant ??
    available[0] ??
    null;

  const switchTo = useCallback(async (id: number) => {
    const previous = tenantStore.get();
    // Set the header first so nothing refetches under the old tenant, then
    // reload: cached lists, the water client/site scope and HR screens all
    // belong to the old tenant (audit HR-32, LG-F-26).
    tenantStore.set(id);
    try {
      await post("/v1/tenants/switch", { tenant_id: id }, { silent: true });
    } catch (e) {
      tenantStore.set(previous);
      throw e;
    }
    // A record page (/…/work-orders/123) belongs to the old tenant and would
    // 404, so land on its list instead of reloading in place (audit FE-30).
    const path = window.location.pathname;
    const listPath = path.replace(/\/\d+(\/.*)?$/, "");
    if (listPath !== path) window.location.assign(listPath || "/");
    else window.location.reload();
  }, []);

  // Another tab switched tenant: this tab's header now points at it while
  // its screen still shows the old one, so reload (audit FE-13).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TENANT_STORAGE_KEY && e.newValue !== e.oldValue) window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);


  const value = useMemo<TenantContextValue>(() => ({ current, available, switchTo }),
    [current, available, switchTo]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const v = useContext(TenantContext);
  if (!v) throw new Error("useTenant must be used inside <TenantProvider>");
  return v;
}

/**
 * The tenant this tab is working in (the X-Tenant-Id every call sends).
 * Use this, not user.current_tenant_id: that column is shared by every
 * tab and device, so it can name a different tenant than the header and
 * org-prefixed URLs then 403 (audit FE-14).
 */
export function useTenantId(): number | null {
  return useTenant().current?.id ?? null;
}
